import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { log, error } from '../../utils/logger';
import { getUserData, setUserData } from '../../utils/encryption';
import { User } from '../../types/auth';
import { fetchUser } from '../../services/api';

interface SecurityAccessCheckProps {
  children: React.ReactNode;
}

/**
 * Component that checks if the user has the required security access
 * If not, redirects to the access denied page
 * Uses both useAuth hook and encrypted localStorage data for double verification
 */
const SecurityAccessCheck: React.FC<SecurityAccessCheckProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [encryptedUserData, setEncryptedUserData] = useState<User | null>(null);

  useEffect(() => {
    // Get latest user data from /me API endpoint
    const fetchLatestUserData = async () => {
      try {
        const user = await fetchUser();

        if (user) {
          log('Successfully fetched latest user data from /me API');
          // Store the latest user data in encrypted storage
          setUserData(user);
          // Update component state
          setEncryptedUserData(user);
        } else {
          // Fallback to encrypted storage if API returns no data
          const userData = getUserData();
          setEncryptedUserData(userData);
        }
      } catch (err) {
        // Log the error
        error('Error fetching user data from /me API:', err);
        
        // Fallback to encrypted storage if API call fails
        const userData = getUserData();
        setEncryptedUserData(userData);
      }
    };

    fetchLatestUserData();
  }, []);

  useEffect(() => {
    // Skip the check if still loading or on the access denied page
    if (isLoading || location.pathname === '/access-denied') {
      return;
    }

    // Get user data from both sources
    const authUser = user;
    const storageUser = encryptedUserData;

    // Check if user has the required security access from either source
    // Primary check from auth context
    const hasSecurityAccessFromAuth = authUser?.access_type?.some(
      (access) => access.toLowerCase().includes('security')
    );

    // Secondary check from encrypted storage
    const hasSecurityAccessFromStorage = storageUser?.access_type?.some(
      (access) => access.toLowerCase().includes('security')
    );

    // Only grant access if both sources confirm security access or if one is unavailable but the other confirms
    const hasSecurityAccess = 
      (hasSecurityAccessFromAuth && hasSecurityAccessFromStorage) || 
      (hasSecurityAccessFromAuth && !storageUser) || 
      (hasSecurityAccessFromStorage && !authUser);

    if (!hasSecurityAccess) {
      log('User does not have security access, redirecting to access denied page');
      navigate('/access-denied', { replace: true });
      return;
    }
  }, [user, encryptedUserData, isLoading, navigate, location.pathname]);

  // If loading, don't render anything yet
  if (isLoading) {
    return null;
  }

  // If on the access denied page, render children regardless of access
  if (location.pathname === '/access-denied') {
    return <>{children}</>;
  }

  // Get user data from both sources
  const authUser = user;
  const storageUser = encryptedUserData;

  // Check if user has the required security access from either source
  const hasSecurityAccessFromAuth = authUser?.access_type?.some(
    (access) => access.toLowerCase().includes('security')
  );

  const hasSecurityAccessFromStorage = storageUser?.access_type?.some(
    (access) => access.toLowerCase().includes('security')
  );

  // Only grant access if both sources confirm security access or if one is unavailable but the other confirms
  const hasSecurityAccess = 
    (hasSecurityAccessFromAuth && hasSecurityAccessFromStorage) || 
    (hasSecurityAccessFromAuth && !storageUser) || 
    (hasSecurityAccessFromStorage && !authUser);

  // If user has security access, render children
  if (hasSecurityAccess) {
    return <>{children}</>;
  }

  // Otherwise, render nothing (will be redirected by the useEffect)
  return null;
};

export default SecurityAccessCheck;